import { useState, useEffect, Fragment } from 'react';
import { useParams, Link } from 'react-router-dom';
import { formatDistanceToNow, format } from 'date-fns';
import api from '../services/api';
import katex from 'katex';
import 'katex/dist/katex.min.css';

function fmtNum(x) {
  return (x === null || x === undefined) ? '—' : x;
}

function ResultBadge({ q }) {
  if (q.is_correct === true) return <span className="text-green-600 font-medium">✓</span>;
  if (q.is_correct === false) return <span className="text-red-600 font-medium">✗</span>;
  return <span className="text-teacher-text-tertiary">—</span>;
}

/**
 * Lazy-loaded per-question detail for one daily quiz or chapter-practice session.
 * Fetches only when rendered (i.e. when its row is expanded).
 */
function QuestionDetail({ userId, type, activityId }) {
  const [questions, setQuestions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const res = await api.getActivityQuestions(userId, type, activityId);
        if (alive) setQuestions(res.data?.questions || []);
      } catch (e) {
        if (alive) setError(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [userId, type, activityId]);

  if (loading) return <div className="py-3 text-sm text-teacher-text-secondary">Loading questions…</div>;
  if (error) return <div className="py-3 text-sm text-red-600">Error: {error}</div>;
  if (!questions || questions.length === 0) return <div className="py-3 text-sm text-teacher-text-secondary">No per-question detail recorded.</div>;

  return (
    <div className="overflow-x-auto bg-teacher-bg rounded-lg p-3 my-1">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-teacher-border text-teacher-text-secondary">
            <th className="text-left py-1.5 px-2 font-medium">#</th>
            <th className="text-left py-1.5 px-2 font-medium">Question ID</th>
            <th className="text-left py-1.5 px-2 font-medium">Subject / Chapter</th>
            <th className="text-center py-1.5 px-2 font-medium">Result</th>
            <th className="text-left py-1.5 px-2 font-medium">Difficulty</th>
            <th className="text-right py-1.5 px-2 font-medium" title="IRT difficulty (b)">b</th>
            <th className="text-right py-1.5 px-2 font-medium" title="Discrimination (a)">a</th>
            <th className="text-right py-1.5 px-2 font-medium" title="Guessing (c)">c</th>
            <th className="text-left py-1.5 px-2 font-medium">Got → Correct</th>
            <th className="text-right py-1.5 px-2 font-medium">Time</th>
            <th className="text-left py-1.5 px-2 font-medium">Answered</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {questions.map((q, i) => (
            <tr key={q.question_id || i} className="align-top">
              <td className="py-1.5 px-2 text-teacher-text-secondary">{q.position != null ? q.position + 1 : i + 1}</td>
              <td className="py-1.5 px-2 font-mono text-teacher-text-primary">{q.question_id}</td>
              <td className="py-1.5 px-2">{q.subject} / {q.chapter}</td>
              <td className="py-1.5 px-2 text-center"><ResultBadge q={q} /></td>
              <td className="py-1.5 px-2 capitalize">{q.difficulty || '—'}</td>
              <td className="py-1.5 px-2 text-right">{fmtNum(q.difficulty_b)}</td>
              <td className="py-1.5 px-2 text-right">{fmtNum(q.discrimination_a)}</td>
              <td className="py-1.5 px-2 text-right">{fmtNum(q.guessing_c)}</td>
              <td className="py-1.5 px-2 text-teacher-text-secondary">{q.student_answer ?? '—'} → {q.correct_answer ?? '—'}</td>
              <td className="py-1.5 px-2 text-right text-teacher-text-secondary">{q.time_taken_seconds != null ? `${q.time_taken_seconds}s` : '—'}</td>
              <td className="py-1.5 px-2 text-teacher-text-secondary whitespace-nowrap">{q.answered_at ? formatDateTime(q.answered_at) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TierBadge({ tier }) {
  const colors = {
    free: 'bg-teacher-bg-secondary text-teacher-text-primary',
    pro: 'bg-blue-100 text-blue-700',
    ultra: 'bg-purple-100 text-purple-700'
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[tier] || colors.free}`}>
      {tier?.toUpperCase() || 'FREE'}
    </span>
  );
}

function StatCard({ label, value, subtext }) {
  return (
    <div className="bg-white rounded-lg border border-teacher-border p-4">
      <div className="text-sm text-teacher-text-secondary mb-1">{label}</div>
      <div className="text-2xl font-bold text-teacher-text-primary">{value}</div>
      {subtext && <div className="text-xs text-teacher-text-tertiary mt-1">{subtext}</div>}
    </div>
  );
}

function formatDuration(seconds) {
  if (!seconds) return '0s';
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  const remainingSeconds = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${remainingMinutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m`;
  }
  return `${remainingSeconds}s`;
}

function formatDate(date) {
  if (!date) return 'N/A';
  try {
    const d = date.toDate ? date.toDate() : new Date(date);
    return format(d, 'MMM dd, yyyy HH:mm');
  } catch {
    return 'N/A';
  }
}

function formatDateTime(dateString) {
  if (!dateString) return 'N/A';
  try {
    const d = new Date(dateString);
    return format(d, 'MMM dd, yyyy HH:mm');
  } catch {
    return 'N/A';
  }
}

/**
 * Render a Priya/student message the way the app does: **bold**, \( \) and
 * \[ \] LaTeX (KaTeX), "---" as a break, paragraphs. Plain text is escaped
 * before any HTML is produced; LaTeX goes through KaTeX which emits its own
 * safe markup.
 */
const escapeHtml = (t) => String(t).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
function renderMessageHtml(content) {
  const src = String(content || '').replace(/\r\n?/g, '\n').replace(/\s---\s/g, '\n\n').replace(/^---\s*$/gm, '');
  const parts = src.split(/(\\\([\s\S]+?\\\)|\\\[[\s\S]+?\\\]|\$\$[\s\S]+?\$\$)/g);
  const html = parts.map((seg, i) => {
    if (i % 2 === 1) {
      const display = seg.startsWith('\\[') || seg.startsWith('$$');
      const tex = seg.slice(2, -2);
      try { return katex.renderToString(tex, { displayMode: display, throwOnError: false }); } catch { return escapeHtml(seg); }
    }
    return escapeHtml(seg)
      // Priya emits a few inline HTML tags herself (the app renders them too); let exactly those through.
      .replace(/&lt;(\/?)(strong|b|em|i|u|sub|sup|br)&gt;/gi, '<$1$2>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/(^|[\s(])\*([^*\n]+)\*(?=[\s).,!?]|$)/g, '$1<em>$2</em>')
      .replace(/(^|\n)(#{1,3})\s+([^\n]+)/g, '$1<strong>$3</strong>')
      .replace(/\n\n+/g, '<br><br>')
      .replace(/\n/g, '<br>');
  }).join('');
  return { __html: html };
}

/**
 * Priya (AI Tutor) conversations — the active thread plus archives written on
 * reset / overflow. Loaded on demand: the content is personal and the page
 * should not fetch it for every student opened.
 */
function TutorConversations({ userId, expectedMessages }) {
  const [state, setState] = useState({ status: 'idle', data: null, error: null });
  const [open, setOpen] = useState({});
  const load = async () => {
    setState({ status: 'loading', data: null, error: null });
    try {
      const res = await api.getTutorConversations(userId);
      setState({ status: 'ready', data: res.data, error: null });
      // The newest thread opens by itself; the rest stay folded.
      const first = res.data.active?.messages?.length ? 'active' : res.data.archived[0]?.id;
      if (first) setOpen({ [first]: true });
    } catch (err) {
      setState({ status: 'error', data: null, error: err.message });
    }
  };
  const threads = state.data
    ? [
        ...(state.data.active && state.data.active.messages.length ? [{ key: 'active', label: 'Current conversation', sub: `since ${formatDateTime(state.data.active.createdAt)}`, messages: state.data.active.messages }] : []),
        ...state.data.archived.map((a) => ({ key: a.id, label: a.reason === 'reset' ? 'Cleared by the student' : 'Trimmed (overflow)', sub: `${formatDateTime(a.firstMessageAt)} → ${formatDateTime(a.lastMessageAt)} · archived ${formatDateTime(a.archivedAt)}`, messages: a.messages })),
      ]
    : [];
  const studentTurns = (msgs) => msgs.filter((m) => m.type === 'user').length;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-teacher-border p-6 mb-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-semibold text-teacher-text-primary">Priya conversations</h2>
        {state.status !== 'ready' && (
          <button
            type="button"
            onClick={load}
            disabled={state.status === 'loading'}
            className="text-sm font-semibold text-primary-700 hover:text-primary-800 disabled:opacity-50"
          >
            {state.status === 'loading' ? 'Loading…' : `Show${expectedMessages ? ` (${expectedMessages} messages)` : ''}`}
          </button>
        )}
      </div>
      <p className="text-xs text-teacher-text-tertiary mb-3">What the student typed to Priya and what she answered. Archived threads are kept when a student clears the chat.</p>
      {state.status === 'error' && <div className="text-sm text-teacher-red">Could not load: {state.error}</div>}
      {state.status === 'ready' && threads.length === 0 && (
        <div className="text-teacher-text-secondary text-center py-4">No messages yet</div>
      )}
      {threads.map((t) => {
        const isOpen = !!open[t.key];
        return (
          <div key={t.key} className="border border-teacher-border rounded-lg mb-3">
            <button type="button" onClick={() => setOpen((o) => ({ ...o, [t.key]: !isOpen }))} className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-teacher-bg">
              <span className="text-sm text-teacher-text-tertiary select-none">{isOpen ? '▾' : '▸'}</span>
              <span className="text-sm font-medium text-teacher-text-primary">{t.label}</span>
              <span className="text-xs text-teacher-text-tertiary">{t.sub}</span>
              <span className="ml-auto text-xs text-teacher-text-secondary whitespace-nowrap">{studentTurns(t.messages)} from student · {t.messages.length} total</span>
            </button>
            {isOpen && (
              <div className="px-3 pb-3 flex flex-col gap-2">
                {t.messages.map((m) => {
                  if (m.type === 'context_marker') {
                    return (
                      <div key={m.id} className="self-center text-[11px] text-teacher-text-tertiary bg-teacher-bg rounded-full px-3 py-0.5">
                        opened from {m.contextType || 'context'}{m.contextTitle ? `: ${m.contextTitle}` : ''} · {formatDateTime(m.timestamp)}
                      </div>
                    );
                  }
                  const mine = m.type === 'user';
                  return (
                    <div key={m.id} className={`max-w-[85%] ${mine ? 'self-end' : 'self-start'}`}>
                      <div
                        className={`rounded-xl px-3 py-2 text-sm break-words leading-relaxed ${mine ? 'bg-primary-50 text-teacher-text-primary' : 'bg-teacher-bg text-teacher-text-primary'}`}
                        dangerouslySetInnerHTML={renderMessageHtml(m.content)}
                      />
                      <div className={`text-[11px] text-teacher-text-tertiary mt-0.5 ${mine ? 'text-right' : ''}`}>
                        {mine ? 'Student' : 'Priya'} · {formatDateTime(m.timestamp)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function UserDetail() {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({}); // `${type}:${id}` -> bool
  const toggleRow = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    async function fetchUserDetails() {
      try {
        setLoading(true);
        const result = await api.getUserDetails(userId);
        setUser(result.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchUserDetails();
  }, [userId]);

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-6">
        <div className="text-teacher-text-secondary">User not found</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <Link to="/users" className="text-primary-600 hover:text-primary-700 text-sm mb-2 inline-block">
          ← Back to Users
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-teacher-text-primary">
              {user.profile.firstName} {user.profile.lastName}
            </h1>
            <p className="text-teacher-text-secondary">{user.profile.email || user.profile.phone}</p>
          </div>
          <TierBadge tier={user.subscription.tier} />
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <StatCard
          label="AI cost (est.)"
          value={user.ai?.costUsd ? `$${user.ai.costUsd.toFixed(2)}` : '$0'}
          subtext={`${user.ai?.snaps || 0} snaps · ${user.ai?.tutorMessages || 0} Priya msgs`}
        />
        <StatCard
          label="Overall Percentile"
          value={`${user.progress.overallPercentile || 0}%`}
          subtext={`θ: ${user.progress.overallTheta?.toFixed(2) || 'N/A'}`}
        />
        <StatCard
          label="Overall Accuracy"
          value={`${user.progress.overallAccuracy || 0}%`}
          subtext="Correct answers"
        />
        <StatCard
          label="Total Questions"
          value={user.progress.totalQuestions}
          subtext={`${user.progress.quizzesCompleted} quizzes`}
        />
        <StatCard
          label="Current Streak"
          value={`${user.streak.current} days`}
          subtext={`Longest: ${user.streak.longest} days`}
        />
        <StatCard
          label="Last Active"
          value={
            user.profile.lastActive
              ? formatDistanceToNow(new Date(user.profile.lastActive), { addSuffix: true })
              : 'Never'
          }
          subtext={user.profile.isEnrolledInCoaching ? 'In coaching' : 'Self study'}
        />
      </div>

      {/* Initial Assessment */}
      {user.assessment && (
        <div className="bg-white rounded-xl shadow-sm border border-teacher-border p-6 mb-6">
          <h2 className="text-lg font-semibold text-teacher-text-primary mb-4">Initial Assessment</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <div className="text-sm text-teacher-text-secondary">Completed</div>
              <div className="text-lg font-medium text-teacher-text-primary">
                {formatDate(user.assessment.completedAt)}
              </div>
            </div>
            <div>
              <div className="text-sm text-teacher-text-secondary">Score</div>
              <div className="text-lg font-medium text-teacher-text-primary">
                {user.assessment.score}/{user.assessment.totalQuestions}
              </div>
            </div>
            <div>
              <div className="text-sm text-teacher-text-secondary">Accuracy</div>
              <div className="text-lg font-medium text-teacher-text-primary">
                {(user.assessment.accuracy * 100).toFixed(1)}%
              </div>
            </div>
            <div>
              <div className="text-sm text-teacher-text-secondary">Time Spent</div>
              <div className="text-lg font-medium text-teacher-text-primary">
                {formatDuration(user.assessment.timeSpentSeconds)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subject Performance */}
      {user.progress.thetaBySubject && (
        <div className="bg-white rounded-xl shadow-sm border border-teacher-border p-6 mb-6">
          <h2 className="text-lg font-semibold text-teacher-text-primary mb-4">Subject Performance</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(user.progress.thetaBySubject).map(([subject, data]) => (
              <div key={subject} className="border border-teacher-border rounded-lg p-4">
                <div className="font-medium text-teacher-text-primary mb-2 capitalize">{subject}</div>
                <div className="text-sm">
                  <div className="text-teacher-text-secondary">Theta (θ)</div>
                  <div className="text-2xl font-bold text-teacher-text-primary mt-1">
                    {data.theta?.toFixed(2) || 'N/A'}
                  </div>
                  {data.percentile && (
                    <div className="text-xs text-teacher-text-secondary mt-1">
                      {data.percentile}th percentile
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Daily Quizzes */}
      <div className="bg-white rounded-xl shadow-sm border border-teacher-border p-6 mb-6">
        <h2 className="text-lg font-semibold text-teacher-text-primary mb-1">
          Daily Quizzes ({user.dailyQuizzes?.length || 0})
        </h2>
        {user.dailyQuizzes && user.dailyQuizzes.length > 0 && (
          <p className="text-xs text-teacher-text-tertiary mb-3">Click a row to see per-question detail (difficulty, IRT params, answers).</p>
        )}
        {user.dailyQuizzes && user.dailyQuizzes.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-teacher-border">
                  <th className="w-8 py-2 px-3"></th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-teacher-text-secondary">Date</th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-teacher-text-secondary">Score</th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-teacher-text-secondary">Accuracy</th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-teacher-text-secondary">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {user.dailyQuizzes.slice(0, 10).map((quiz, idx) => {
                  const key = `daily_quiz:${quiz.id}`;
                  const isOpen = !!expanded[key];
                  return (
                    <Fragment key={quiz.id || idx}>
                      <tr
                        className={`hover:bg-teacher-bg ${quiz.id ? 'cursor-pointer' : ''}`}
                        onClick={() => quiz.id && toggleRow(key)}
                      >
                        <td className="py-2 px-3 text-sm text-teacher-text-tertiary select-none">{isOpen ? '▾' : '▸'}</td>
                        <td className="py-2 px-3 text-sm">{formatDate(quiz.completedAt)}</td>
                        <td className="py-2 px-3 text-sm">
                          {quiz.score}/{quiz.totalQuestions}
                        </td>
                        <td className="py-2 px-3 text-sm">
                          <span className={`font-medium ${
                            quiz.accuracy >= 0.8 ? 'text-green-600' :
                            quiz.accuracy >= 0.6 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {(quiz.accuracy * 100).toFixed(1)}%
                          </span>
                        </td>
                        <td className="py-2 px-3 text-sm text-teacher-text-secondary">
                          {formatDuration(quiz.timeSpentSeconds)}
                        </td>
                      </tr>
                      {isOpen && quiz.id && (
                        <tr>
                          <td colSpan={5} className="px-3 pb-2">
                            <QuestionDetail userId={userId} type="daily_quiz" activityId={quiz.id} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
            {user.dailyQuizzes.length > 10 && (
              <div className="text-sm text-teacher-text-secondary mt-2 text-center">
                Showing 10 of {user.dailyQuizzes.length} quizzes
              </div>
            )}
          </div>
        ) : (
          <div className="text-teacher-text-secondary text-center py-4">No daily quizzes completed yet</div>
        )}
      </div>

      {/* Chapter Practice */}
      <div className="bg-white rounded-xl shadow-sm border border-teacher-border p-6 mb-6">
        <h2 className="text-lg font-semibold text-teacher-text-primary mb-1">
          Chapter Practice ({user.chapterPractice?.length || 0})
        </h2>
        {user.chapterPractice && user.chapterPractice.length > 0 && (
          <p className="text-xs text-teacher-text-tertiary mb-3">Click a row to see per-question detail (difficulty, IRT params, answers).</p>
        )}
        {user.chapterPractice && user.chapterPractice.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-teacher-border">
                  <th className="w-8 py-2 px-3"></th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-teacher-text-secondary">Chapter</th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-teacher-text-secondary">Subject</th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-teacher-text-secondary">Score</th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-teacher-text-secondary">Accuracy</th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-teacher-text-secondary">Time</th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-teacher-text-secondary">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {user.chapterPractice.slice(0, 10).map((session, idx) => {
                  const key = `chapter_practice:${session.id}`;
                  const isOpen = !!expanded[key];
                  return (
                    <Fragment key={session.id || idx}>
                      <tr
                        className={`hover:bg-teacher-bg ${session.id ? 'cursor-pointer' : ''}`}
                        onClick={() => session.id && toggleRow(key)}
                      >
                        <td className="py-2 px-3 text-sm text-teacher-text-tertiary select-none">{isOpen ? '▾' : '▸'}</td>
                        <td className="py-2 px-3 text-sm">{session.chapterName}</td>
                        <td className="py-2 px-3 text-sm capitalize">{session.subject}</td>
                        <td className="py-2 px-3 text-sm">
                          {session.score}/{session.totalQuestions}
                        </td>
                        <td className="py-2 px-3 text-sm">
                          <span className={`font-medium ${
                            session.accuracy >= 0.8 ? 'text-green-600' :
                            session.accuracy >= 0.6 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {(session.accuracy * 100).toFixed(1)}%
                          </span>
                        </td>
                        <td className="py-2 px-3 text-sm text-teacher-text-secondary">
                          {formatDuration(session.timeSpentSeconds)}
                        </td>
                        <td className="py-2 px-3 text-sm text-teacher-text-secondary">
                          {formatDate(session.completedAt)}
                        </td>
                      </tr>
                      {isOpen && session.id && (
                        <tr>
                          <td colSpan={7} className="px-3 pb-2">
                            <QuestionDetail userId={userId} type="chapter_practice" activityId={session.id} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
            {user.chapterPractice.length > 10 && (
              <div className="text-sm text-teacher-text-secondary mt-2 text-center">
                Showing 10 of {user.chapterPractice.length} sessions
              </div>
            )}
          </div>
        ) : (
          <div className="text-teacher-text-secondary text-center py-4">No chapter practice sessions yet</div>
        )}
      </div>

      {/* Mock Tests */}
      <div className="bg-white rounded-xl shadow-sm border border-teacher-border p-6 mb-6">
        <h2 className="text-lg font-semibold text-teacher-text-primary mb-4">
          Mock Tests ({user.mockTests?.length || 0})
        </h2>
        {user.mockTests && user.mockTests.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-teacher-border">
                  <th className="text-left py-2 px-3 text-sm font-medium text-teacher-text-secondary">Test Name</th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-teacher-text-secondary">Score</th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-teacher-text-secondary">Accuracy</th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-teacher-text-secondary">Time</th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-teacher-text-secondary">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {user.mockTests.map((test, idx) => (
                  <tr key={test.id || idx} className="hover:bg-teacher-bg">
                    <td className="py-2 px-3 text-sm">{test.testName}</td>
                    <td className="py-2 px-3 text-sm">
                      {test.score}/{test.maxScore}
                    </td>
                    <td className="py-2 px-3 text-sm">
                      <span className={`font-medium ${
                        test.accuracy >= 0.8 ? 'text-green-600' :
                        test.accuracy >= 0.6 ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {(test.accuracy * 100).toFixed(1)}%
                      </span>
                    </td>
                    <td className="py-2 px-3 text-sm text-teacher-text-secondary">
                      {formatDuration(test.timeSpentSeconds)}
                    </td>
                    <td className="py-2 px-3 text-sm text-teacher-text-secondary">
                      {formatDate(test.completedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-teacher-text-secondary text-center py-4">No mock tests completed yet</div>
        )}
      </div>

      {/* Priya conversations (on demand — PII) */}
      <TutorConversations userId={userId} expectedMessages={user.ai?.tutorMessages || 0} />

      {/* Percentile History - Daily Trend */}
      <div className="bg-white rounded-xl shadow-sm border border-teacher-border p-6 mb-6">
        <h2 className="text-lg font-semibold text-teacher-text-primary mb-4">
          Percentile Trend
        </h2>
        {user.percentileHistory && user.percentileHistory.length > 0 ? (
          <div className="overflow-x-auto">
            <div className="text-sm text-teacher-text-secondary mb-3">
              Last {user.percentileHistory.length} {user.percentileHistory.length === 1 ? 'entry' : 'entries'}
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-teacher-border">
                  <th className="text-left py-2 px-3 text-sm font-medium text-teacher-text-secondary">Date & Time</th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-teacher-text-secondary">Percentile</th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-teacher-text-secondary">Theta (θ)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {user.percentileHistory.slice().reverse().slice(0, 30).map((item, idx) => (
                  <tr key={item.date || idx} className="hover:bg-teacher-bg">
                    <td className="py-2 px-3 text-sm text-teacher-text-secondary">{formatDateTime(item.date)}</td>
                    <td className="py-2 px-3 text-sm">
                      <span className={`font-medium ${
                        item.percentile >= 80 ? 'text-green-600' :
                        item.percentile >= 60 ? 'text-blue-600' :
                        item.percentile >= 40 ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {item.percentile}%
                      </span>
                    </td>
                    <td className="py-2 px-3 text-sm text-teacher-text-primary">{item.theta || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {user.percentileHistory.length > 30 && (
              <div className="text-sm text-teacher-text-secondary mt-2 text-center">
                Showing most recent 30 of {user.percentileHistory.length} entries
              </div>
            )}
          </div>
        ) : (
          <div className="text-teacher-text-secondary text-center py-4">
            No percentile history yet. Snapshots are created after completing daily quizzes.
          </div>
        )}
      </div>

      {/* Accuracy History - Daily Trend */}
      <div className="bg-white rounded-xl shadow-sm border border-teacher-border p-6">
        <h2 className="text-lg font-semibold text-teacher-text-primary mb-4">
          Accuracy Trend
        </h2>
        {user.accuracyHistory && user.accuracyHistory.length > 0 ? (
          <div className="overflow-x-auto">
            <div className="text-sm text-teacher-text-secondary mb-3">
              Last {user.accuracyHistory.length} {user.accuracyHistory.length === 1 ? 'entry' : 'entries'} • Current: {user.progress.overallAccuracy || 0}%
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-teacher-border">
                  <th className="text-left py-2 px-3 text-sm font-medium text-teacher-text-secondary">Date & Time</th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-teacher-text-secondary">Accuracy</th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-teacher-text-secondary">Questions (Correct/Total)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {user.accuracyHistory.slice().reverse().slice(0, 30).map((item, idx) => (
                  <tr key={item.date || idx} className="hover:bg-teacher-bg">
                    <td className="py-2 px-3 text-sm text-teacher-text-secondary">{formatDateTime(item.date)}</td>
                    <td className="py-2 px-3 text-sm">
                      <span className={`font-medium ${
                        item.accuracy >= 85 ? 'text-green-600' :
                        item.accuracy >= 70 ? 'text-blue-600' :
                        item.accuracy >= 50 ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {item.accuracy}%
                      </span>
                    </td>
                    <td className="py-2 px-3 text-sm text-teacher-text-secondary">
                      {item.correct || 0}/{item.total || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {user.accuracyHistory.length > 30 && (
              <div className="text-sm text-teacher-text-secondary mt-2 text-center">
                Showing most recent 30 of {user.accuracyHistory.length} entries
              </div>
            )}
          </div>
        ) : (
          <div className="text-teacher-text-secondary text-center py-4">
            No accuracy history yet. Data will appear after completing daily quizzes with the new system.
          </div>
        )}
      </div>
    </div>
  );
}
